import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Search, Loader2, CheckCircle, Save, X, User, Shield, Users } from 'lucide-react';
import { taskRequestApi } from '@features/tasks/services/taskRequestApi';
import {
  pagePermissionApi,
  type PermissionGroup,
  type GroupPermissionAction,
} from '@shared/services/pagePermissionApi';
import type { UserSearchResult } from '@features/tasks/types/taskRequest';
import './UserPagePermissionsTab.css';

type Mode = 'group-permissions' | 'user-groups';

/** Canonical action names for column ordering. */
const ACTION_COLUMNS = ['View', 'Create', 'Update', 'Delete'] as const;

/**
 * Build the permissions grid: rows grouped by area, columns by action.
 * Returns a map of areaName -> { actionName -> GroupPermissionAction }.
 */
function buildGrid(actions: GroupPermissionAction[]) {
  const areas = new Map<string, Map<string, GroupPermissionAction>>();
  for (const action of actions) {
    if (!areas.has(action.areaName)) {
      areas.set(action.areaName, new Map());
    }
    areas.get(action.areaName)!.set(action.actionName, action);
  }
  return areas;
}

export default function UserPagePermissionsTab() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('group-permissions');

  return (
    <div className="upp-tab">
      {/* Header */}
      <div className="upp-header">
        <Lock size={18} />
        <div>
          <h2 className="upp-title">Permission Management</h2>
          <p className="upp-desc">
            Manage group-based permissions and assign users to permission groups.
          </p>
        </div>
      </div>

      {/* Mode Tabs */}
      <div className="upp-mode-tabs">
        <button
          type="button"
          className={`upp-mode-tab ${mode === 'group-permissions' ? 'upp-mode-tab--active' : ''}`}
          onClick={() => setMode('group-permissions')}
        >
          <Shield size={14} />
          Group Permissions
        </button>
        <button
          type="button"
          className={`upp-mode-tab ${mode === 'user-groups' ? 'upp-mode-tab--active' : ''}`}
          onClick={() => setMode('user-groups')}
        >
          <Users size={14} />
          User Groups
        </button>
      </div>

      {mode === 'group-permissions' ? (
        <GroupPermissionsPanel queryClient={queryClient} />
      ) : (
        <UserGroupsPanel queryClient={queryClient} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Group Permissions Panel
// ─────────────────────────────────────────────────────────────────────────────

interface PanelProps {
  queryClient: ReturnType<typeof useQueryClient>;
}

function GroupPermissionsPanel({ queryClient }: Readonly<PanelProps>) {
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [grantedIds, setGrantedIds] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ─── Fetch groups ───
  const { data: groups, isLoading: loadingGroups } = useQuery<PermissionGroup[]>({
    queryKey: ['permission-groups'],
    queryFn: () => pagePermissionApi.getGroups(),
    staleTime: 10 * 60 * 1000,
  });

  // ─── Fetch selected group's permissions ───
  const {
    data: groupActions,
    isLoading: loadingActions,
    refetch: refetchActions,
  } = useQuery<GroupPermissionAction[]>({
    queryKey: ['group-permissions', selectedGroupId],
    queryFn: () => pagePermissionApi.getGroupPermissions(selectedGroupId!),
    enabled: selectedGroupId !== null,
  });

  // ─── Sync grantedIds when groupActions loads ───
  useEffect(() => {
    if (groupActions) {
      setGrantedIds(new Set(groupActions.filter((a) => a.isGranted).map((a) => a.actionId)));
    }
  }, [groupActions]);

  // ─── Build the grid ───
  const grid = useMemo(() => (groupActions ? buildGrid(groupActions) : new Map()), [groupActions]);

  // ─── Derived: detect changes ───
  const originalGrantedIds = useMemo(() => {
    if (!groupActions) return new Set<number>();
    return new Set(groupActions.filter((a) => a.isGranted).map((a) => a.actionId));
  }, [groupActions]);

  const hasChanges = useMemo(() => {
    if (originalGrantedIds.size !== grantedIds.size) return true;
    for (const id of grantedIds) {
      if (!originalGrantedIds.has(id)) return true;
    }
    return false;
  }, [originalGrantedIds, grantedIds]);

  // ─── Handlers ───

  const handleGroupChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedGroupId(val ? Number(val) : null);
    setGrantedIds(new Set());
    setSaveMessage(null);
  }, []);

  const handleToggleAction = useCallback((actionId: number) => {
    setGrantedIds((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
    setSaveMessage(null);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!groupActions) return;
    setGrantedIds(new Set(groupActions.map((a) => a.actionId)));
    setSaveMessage(null);
  }, [groupActions]);

  const handleDeselectAll = useCallback(() => {
    setGrantedIds(new Set());
    setSaveMessage(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (selectedGroupId === null) return;
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await pagePermissionApi.setGroupPermissions(selectedGroupId, Array.from(grantedIds));
      await refetchActions();
      void queryClient.invalidateQueries({ queryKey: ['page-permissions-me'] });
      setSaveMessage({ type: 'success', text: 'Group permissions saved successfully.' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Failed to save group permissions. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  }, [selectedGroupId, grantedIds, refetchActions, queryClient]);

  return (
    <>
      {/* Group Selector */}
      <div className="upp-search-section">
        <label className="upp-label" htmlFor="upp-group-select">
          <Shield size={14} />
          Select Group
        </label>
        <select
          id="upp-group-select"
          className="upp-group-select"
          value={selectedGroupId ?? ''}
          onChange={handleGroupChange}
          disabled={loadingGroups}
        >
          <option value="">
            {loadingGroups ? 'Loading groups...' : '-- Select a group --'}
          </option>
          {(groups ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}{g.contextRestricted ? ' (context-restricted)' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Permissions Grid */}
      {selectedGroupId !== null && (
        <div className="upp-permissions-section">
          <div className="upp-permissions-header">
            <h3 className="upp-permissions-title">Permissions</h3>
          </div>

          {loadingActions ? (
            <div className="upp-loading">
              <Loader2 size={16} className="upp-spinning" />
              Loading permissions...
            </div>
          ) : grid.size === 0 ? (
            <div className="upp-empty">No permission actions available for this group.</div>
          ) : (
            <>
              {/* Bulk actions */}
              <div className="upp-bulk-actions">
                <button type="button" className="upp-btn upp-btn-sm" onClick={handleSelectAll}>
                  Select All
                </button>
                <button type="button" className="upp-btn upp-btn-sm" onClick={handleDeselectAll}>
                  Deselect All
                </button>
              </div>

              {/* Grid table */}
              <table className="upp-grid">
                <thead>
                  <tr>
                    <th className="upp-grid-area-header">Area</th>
                    {ACTION_COLUMNS.map((col) => (
                      <th key={col} className="upp-grid-action-header">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from(grid.entries()).map(([areaName, actionMap]) => (
                    <tr key={areaName}>
                      <td className="upp-grid-area-cell">{areaName}</td>
                      {ACTION_COLUMNS.map((actionName) => {
                        const action = actionMap.get(actionName);
                        if (!action) {
                          return <td key={actionName} className="upp-grid-cell upp-grid-cell--na" />;
                        }
                        return (
                          <td key={actionName} className="upp-grid-cell">
                            <input
                              type="checkbox"
                              className="upp-grid-check"
                              checked={grantedIds.has(action.actionId)}
                              onChange={() => handleToggleAction(action.actionId)}
                              title={`${actionName} ${areaName}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Save message */}
              {saveMessage && (
                <div className={`upp-message upp-message-${saveMessage.type}`}>
                  {saveMessage.type === 'success' ? <CheckCircle size={14} /> : <X size={14} />}
                  {saveMessage.text}
                </div>
              )}

              {/* Action buttons */}
              <div className="upp-actions">
                <button
                  type="button"
                  className="upp-btn upp-btn-primary"
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                >
                  {isSaving ? (
                    <><Loader2 size={14} className="upp-spinning" /> Saving...</>
                  ) : (
                    <><Save size={14} /> Save Permissions</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// User Groups Panel
// ─────────────────────────────────────────────────────────────────────────────

function UserGroupsPanel({ queryClient }: Readonly<PanelProps>) {
  // ─── User search state ───
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Selected user state ───
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ─── Fetch all groups ───
  const { data: groups, isLoading: loadingGroups } = useQuery<PermissionGroup[]>({
    queryKey: ['permission-groups'],
    queryFn: () => pagePermissionApi.getGroups(),
    staleTime: 10 * 60 * 1000,
  });

  // ─── Fetch selected user's groups ───
  const {
    data: userGroupIds,
    isLoading: loadingUserGroups,
    refetch: refetchUserGroups,
  } = useQuery<number[]>({
    queryKey: ['user-groups', selectedUser?.userId],
    queryFn: () => pagePermissionApi.getUserGroups(selectedUser!.userId),
    enabled: selectedUser !== null,
  });

  // ─── Sync selectedGroupIds when userGroupIds loads ───
  useEffect(() => {
    if (userGroupIds) {
      setSelectedGroupIds(new Set(userGroupIds));
    }
  }, [userGroupIds]);

  // ─── Debounced user search ───
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await taskRequestApi.searchUsers(searchQuery.trim(), 10);
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchQuery]);

  // ─── Derived: detect changes ───
  const originalGroupIds = useMemo(() => new Set(userGroupIds ?? []), [userGroupIds]);

  const hasChanges = useMemo(() => {
    if (originalGroupIds.size !== selectedGroupIds.size) return true;
    for (const id of selectedGroupIds) {
      if (!originalGroupIds.has(id)) return true;
    }
    return false;
  }, [originalGroupIds, selectedGroupIds]);

  // ─── Handlers ───

  const handleSelectUser = useCallback((user: UserSearchResult) => {
    setSelectedUser(user);
    setSearchQuery('');
    setSearchResults([]);
    setSaveMessage(null);
    setSelectedGroupIds(new Set());
  }, []);

  const handleClearUser = useCallback(() => {
    setSelectedUser(null);
    setSelectedGroupIds(new Set());
    setSaveMessage(null);
  }, []);

  const handleToggleGroup = useCallback((groupId: number) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
    setSaveMessage(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await pagePermissionApi.setUserGroups(selectedUser.userId, Array.from(selectedGroupIds));
      await refetchUserGroups();
      void queryClient.invalidateQueries({ queryKey: ['page-permissions-me'] });
      setSaveMessage({ type: 'success', text: 'User group assignments saved successfully.' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Failed to save group assignments. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  }, [selectedUser, selectedGroupIds, refetchUserGroups, queryClient]);

  return (
    <>
      {/* User Search */}
      <div className="upp-search-section">
        <label className="upp-label" htmlFor="upp-user-search">
          <Search size={14} />
          Find User
        </label>

        {selectedUser ? (
          <div className="upp-selected-user">
            <div className="upp-selected-user-info">
              <User size={16} />
              <div>
                <span className="upp-selected-user-name">{selectedUser.displayName}</span>
                <span className="upp-selected-user-email">{selectedUser.email}</span>
              </div>
            </div>
            <button
              type="button"
              className="upp-btn upp-btn-icon"
              onClick={handleClearUser}
              title="Clear selection"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <>
            <input
              id="upp-user-search"
              type="text"
              className="upp-search-input"
              placeholder="Type a name or email..."
              aria-label="Search users"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />

            {/* Search results dropdown */}
            {(isSearching || searchResults.length > 0 || (searchQuery.trim() && !isSearching)) && (
              <div className="upp-search-results">
                {isSearching ? (
                  <div className="upp-search-empty">
                    <Loader2 size={14} className="upp-spinning" />
                    Searching...
                  </div>
                ) : searchResults.length === 0 && searchQuery.trim() ? (
                  <div className="upp-search-empty">No users found</div>
                ) : (
                  searchResults.map((user) => (
                    <button
                      type="button"
                      key={user.userId}
                      className="upp-search-result-item"
                      onClick={() => handleSelectUser(user)}
                    >
                      <span className="upp-search-result-name">{user.displayName}</span>
                      <span className="upp-search-result-email">{user.email}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Group Assignment Checkboxes */}
      {selectedUser && (
        <div className="upp-permissions-section">
          <div className="upp-permissions-header">
            <h3 className="upp-permissions-title">Group Membership</h3>
          </div>

          {loadingGroups || loadingUserGroups ? (
            <div className="upp-loading">
              <Loader2 size={16} className="upp-spinning" />
              Loading groups...
            </div>
          ) : !groups || groups.length === 0 ? (
            <div className="upp-empty">No permission groups available.</div>
          ) : (
            <>
              {/* Checkbox list */}
              <div className="upp-checkbox-grid">
                {groups.map((group) => (
                  <label key={group.id} className="upp-checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedGroupIds.has(group.id)}
                      onChange={() => handleToggleGroup(group.id)}
                    />
                    <div className="upp-checkbox-content">
                      <span className="upp-checkbox-label">{group.name}</span>
                      {group.contextRestricted && (
                        <span className="upp-checkbox-desc">Context-restricted</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {/* Save message */}
              {saveMessage && (
                <div className={`upp-message upp-message-${saveMessage.type}`}>
                  {saveMessage.type === 'success' ? <CheckCircle size={14} /> : <X size={14} />}
                  {saveMessage.text}
                </div>
              )}

              {/* Action buttons */}
              <div className="upp-actions">
                <button
                  type="button"
                  className="upp-btn upp-btn-primary"
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                >
                  {isSaving ? (
                    <><Loader2 size={14} className="upp-spinning" /> Saving...</>
                  ) : (
                    <><Save size={14} /> Save Group Assignments</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}